import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, Sparkles, Building2, User } from "lucide-react";

const L = {
  en: { kicker: "PRICING · 7-DAY FREE TRIAL", title: "Pick your level", subtitle: "Every plan starts with 7 days free. Cancel anytime. Card required to start the trial — you won't be charged before day 8.",
    monthly: "Monthly", yearly: "Yearly · −20%", perMonth: "/mo", perYear: "/yr", players: "Players", clubs: "Clubs", start: "Start 7-day trial", current: "Current plan", login: "Sign up to start", popular: "Most popular",
    sponsored: "Your club sponsors your Pro access", unlimited: "Unlimited", perMo: "/month", upTo: "Up to", playersUnit: "players",
    f: { coach: "AI Coach messages", avatar: "AI avatars", video: "Video vision analyses", programs: "AI training programs", report: "Weekly AI report", protocol: "Pain alerts + rest protocol", share: "Share your twin card", compare: "Video side-by-side compare", base: "Daily log · Medical map · Stats · Journal", clubBase: "Teams, join codes, live roster, alerts, player detail", sponsor: "Every player in your club gets Pro for free" } },
  fr: { kicker: "TARIFS · 7 JOURS D'ESSAI GRATUIT", title: "Choisis ton niveau", subtitle: "Chaque plan démarre avec 7 jours gratuits. Annulable à tout moment. Carte requise pour lancer l'essai — aucun prélèvement avant le 8e jour.",
    monthly: "Mensuel", yearly: "Annuel · −20 %", perMonth: "/mois", perYear: "/an", players: "Joueurs", clubs: "Clubs", start: "Démarrer l'essai 7 jours", current: "Plan actuel", login: "S'inscrire pour démarrer", popular: "Le plus populaire",
    sponsored: "Ton club t'offre l'accès Pro", unlimited: "Illimité", perMo: "/mois", upTo: "Jusqu'à", playersUnit: "joueurs",
    f: { coach: "Messages Coach IA", avatar: "Avatars IA", video: "Analyses vidéo vision", programs: "Programmes d'entraînement IA", report: "Rapport IA hebdo", protocol: "Alertes douleur + protocole de repos", share: "Carte de partage du jumeau", compare: "Comparaison vidéo côte à côte", base: "Suivi quotidien · Carte médicale · Stats · Journal", clubBase: "Équipes, codes d'invitation, effectif live, alertes, fiche joueur", sponsor: "Chaque joueur de ton club reçoit Pro gratuitement" } },
};

const eur = (cents) => (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

const Feature = ({ ok, label, value }) => (
  <li className={`flex items-start gap-2 text-sm ${ok ? "text-slate-200" : "text-slate-600 line-through"}`}>
    {ok ? <Check className="w-4 h-4 text-[#00F076] mt-0.5 shrink-0" /> : <X className="w-4 h-4 mt-0.5 shrink-0" />}
    <span>{label}{value != null && <span className="text-[#00F076] font-mono-tech"> · {value}</span>}</span>
  </li>
);

export default function Pricing() {
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const { user, billing } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [tab, setTab] = useState(sp.get("tab") || (user?.role === "club" ? "club" : "player"));
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/billing/plans").then((r) => setData(r.data)); }, []);
  useEffect(() => { if (user?.role) setTab(user.role); }, [user]);

  const checkout = async (planId) => {
    if (!user) return nav(`/auth?mode=register&role=${tab}`);
    setBusy(planId);
    try {
      const r = await api.post("/billing/checkout", { lookup_key: `${planId}_${yearly ? "yearly" : "monthly"}`, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "already_subscribed") nav("/billing");
      else toast.error(typeof d === "string" ? d : "Checkout failed");
      setBusy(null);
    }
  };

  const plans = (data?.plans || []).filter((p) => p.role === tab);
  const fmtLimit = (v) => (v === null ? t.unlimited : v);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">{t.kicker}</div>
        <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl mt-2" data-testid="pricing-title">{t.title}</h1>
        <p className="text-slate-400 mt-3">{t.subtitle}</p>
        {billing?.sponsored_by && <div className="mt-4 inline-block px-4 py-2 rounded-full border border-[#00F076]/40 bg-[#00F076]/10 text-[#00F076] text-xs font-mono-tech uppercase" data-testid="pricing-sponsored">{t.sponsored} · {billing.sponsored_by.club_name}</div>}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        {!user && (
          <div className="inline-flex rounded-lg border border-[#1E2638] bg-[#121721] p-1" data-testid="pricing-role-tabs">
            {[["player", t.players, User], ["club", t.clubs, Building2]].map(([id, lbl, Icon]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`pricing-tab-${id}`} className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider ${tab === id ? "bg-[#00F076] text-black" : "text-slate-400"}`}><Icon className="w-3.5 h-3.5" />{lbl}</button>
            ))}
          </div>
        )}
        <div className="inline-flex rounded-lg border border-[#1E2638] bg-[#121721] p-1" data-testid="pricing-interval-toggle">
          <button onClick={() => setYearly(false)} data-testid="pricing-monthly" className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider ${!yearly ? "bg-[#00F076] text-black" : "text-slate-400"}`}>{t.monthly}</button>
          <button onClick={() => setYearly(true)} data-testid="pricing-yearly" className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider ${yearly ? "bg-[#00F076] text-black" : "text-slate-400"}`}>{t.yearly}</button>
        </div>
      </div>

      <div className={`mt-10 grid gap-6 ${tab === "club" ? "md:grid-cols-2 max-w-4xl mx-auto" : "md:grid-cols-3"}`}>
        {plans.map((p, i) => {
          const popular = p.id === "pro" || p.id === "academy";
          const isCurrent = billing?.plan === p.id && billing?.status !== "sponsored";
          const price = yearly ? p.yearly_cents : p.monthly_cents;
          const lim = p.limits;
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`} className={`relative rounded-2xl border p-7 flex flex-col ${popular ? "border-[#00F076] bg-[#00F076]/5 shadow-[0_0_60px_-20px_#00F076]" : "border-[#1E2638] bg-[#121721]"}`} style={{ animationDelay: `${i * 80}ms` }}>
              {popular && <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-[#00F076] text-black text-[10px] font-bold uppercase tracking-wider">{t.popular}</div>}
              <div className="font-display font-black uppercase text-2xl">{p.name}</div>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display font-black text-5xl" data-testid={`plan-price-${p.id}`}>{eur(price)}</span>
                <span className="text-slate-400 text-sm mb-2">{yearly ? t.perYear : t.perMonth}</span>
              </div>
              {yearly && <div className="text-[10px] font-mono-tech text-[#00F076] mt-1">≈ {eur(Math.round(price / 12))}{t.perMo}</div>}
              <ul className="mt-6 space-y-2 flex-1">
                {p.role === "player" ? (
                  <>
                    <Feature ok label={t.f.base} />
                    <Feature ok label={t.f.coach} value={fmtLimit(lim.coach) + (lim.coach !== null ? t.perMo : "")} />
                    <Feature ok label={t.f.avatar} value={fmtLimit(lim.avatar) + (lim.avatar !== null ? t.perMo : "")} />
                    <Feature ok label={t.f.programs} value={fmtLimit(lim.programs) + (lim.programs !== null ? t.perMo : "")} />
                    <Feature ok={lim.video !== 0} label={t.f.video} value={lim.video === 0 ? null : fmtLimit(lim.video) + (lim.video !== null ? t.perMo : "")} />
                    <Feature ok={lim.report} label={t.f.report} />
                    <Feature ok={lim.protocol} label={t.f.protocol} />
                    <Feature ok={lim.share} label={t.f.share} />
                    <Feature ok={lim.compare} label={t.f.compare} />
                  </>
                ) : (
                  <>
                    <Feature ok label={`${t.upTo} ${lim.max_players} ${t.playersUnit}`} />
                    <Feature ok label={t.f.clubBase} />
                    <Feature ok label={t.f.sponsor} />
                  </>
                )}
              </ul>
              <Button onClick={() => checkout(p.id)} disabled={busy === p.id || isCurrent} data-testid={`plan-cta-${p.id}`} className={`mt-6 w-full font-bold uppercase tracking-wider ${popular ? "bg-[#00F076] hover:bg-[#00C963] text-black" : "bg-white/10 hover:bg-white/20 text-white"}`}>
                <Sparkles className="w-4 h-4 mr-2" />{isCurrent ? t.current : busy === p.id ? "…" : user ? t.start : t.login}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
